import { usePathname } from 'next/navigation';
import {
  segmentImpressionId,
  trackSegmentTrackEvent,
} from '~/app/lib/segment-analytics';

import appConfig from '@nerdwallet/app-config';
import { FinishType } from '@nerdwallet/react-apply-now';
import { useAnalyticsPageView } from '@nerdwallet/seo-analytics';
import { useDeviceType } from '~/app/lib/get-device-type';
import { baseSegmentAnalytics } from '~/app/lib/utils';

export type MarketplaceButtonProps = {
  device_type?: string;
  impression_page_url: string;
  impression_pageview_id: string;
  product_name?: string;
  monetizable: 'YES_ASSUMED';
  impression_section?: string;
  impression_position?: string;
  sku_id?: string;
  sku_impression_id: string;
  sku_name?: string;
  sku_level?: string;
  sku_product_type?: string;
  sku_source: 'marketplace';
  entity_name?: string;
  location: string;
  sub_location: string;
  cta_type: 'apply_now_button';
  finish_type: typeof FinishType.EXTERNAL_APPLICATION;
  calling_deployable_name?: string;
  calling_deployable_version?: string;
  shopping_finish_id?: string;
} & Record<string, any>;

export const addCtaApplyNowLinkTracking = (
  marketplaceEntity: any,
  searchParams: object
) => {
  if (marketplaceEntity?.cta?.link) {
    const updatedUrl = new URL(marketplaceEntity.cta?.link);
    Object.keys(searchParams).forEach((key) => {
      // Prevent to append undefined/null params
      if (searchParams[key as keyof typeof searchParams]) {
        updatedUrl.searchParams.append(
          key,
          searchParams[key as keyof typeof searchParams]
        );
      }
    });
    return {
      ...marketplaceEntity,
      cta: {
        ...marketplaceEntity.cta,
        link: updatedUrl.toString(),
      },
    };
  }
  return marketplaceEntity;
};

export const useDynamicAnalytics = ({
  enableInteractionTracking = false,
  extraProps = {},
  impressionPosition,
  impressionSection,
  isApplyNowButtonType = false,
  selectedMarketplaceOffer,
  location,
  subLocation,
  entityName,
}: {
  enableInteractionTracking?: boolean;
  entityName?: string;
  extraProps?: Record<string, any>;
  impressionPosition?: string;
  impressionSection?: string;
  isApplyNowButtonType?: boolean;
  modalId?: string;
  selectedMarketplaceOffer?: any;
  location?: string;
  subLocation?: string;
}) => {
  const pathname = usePathname();
  const { pageViewId } = useAnalyticsPageView();
  const deviceType = useDeviceType();

  // TODO: check if we need this for Button
  // const skuViewedProps = {
  //   sku_id: selectedMarketplaceOffer?.id ?? entityName,
  //   sku_level: selectedMarketplaceOffer?.productType ?? entityName,
  //   sku_name: selectedMarketplaceOffer?.name ?? entityName,
  //   sku_source: 'marketplace',
  //   sku_impression_id: segmentImpressionId,
  //   monetizable: 'YES_ASSUMED',
  //   sku_product_type: modalId ? `modal_${modalId}_open` : 'cta_button',
  // };

  const locationProps = {
    ...(location && { location }),
    ...(subLocation && { sub_location: subLocation }),
  };

  const optionalImpressionProps = {
    ...(impressionSection && { impression_section: impressionSection }),
    ...(impressionPosition && { impression_position: impressionPosition }),
  };

  const impressionViewProps = {
    ...baseSegmentAnalytics,
    ...locationProps,
    ...optionalImpressionProps,
    device_type: deviceType,
    impression_id: segmentImpressionId,
    impression_page_url: pathname,
    impression_pageview_id: pageViewId,
    entity_name: selectedMarketplaceOffer?.name || entityName,
    finish_type: FinishType.EXTERNAL_APPLICATION,
    // eslint-disable-next-line no-underscore-dangle
    calling_deployable_name: appConfig?._meta?.deployable,
    // eslint-disable-next-line no-underscore-dangle
    calling_deployable_version: appConfig?._meta?.version,
    ...extraProps,
    // ...skuViewedProps,
  };

  const marketplaceButtonProps: MarketplaceButtonProps = {
    device_type: deviceType,
    impression_page_url: pathname,
    impression_pageview_id: pageViewId,
    product_name: selectedMarketplaceOffer?.name,
    monetizable: 'YES_ASSUMED',
    impression_section: impressionSection,
    impression_position: impressionPosition,
    sku_id: selectedMarketplaceOffer?.id,
    sku_impression_id: segmentImpressionId,
    sku_name: selectedMarketplaceOffer?.name,
    sku_level: selectedMarketplaceOffer?.productType,
    sku_product_type: selectedMarketplaceOffer?.productType,
    sku_source: 'marketplace',
    entity_name: selectedMarketplaceOffer?.name,
    location: pathname,
    sub_location: '',
    cta_type: 'apply_now_button',
    finish_type: FinishType.EXTERNAL_APPLICATION,
    // eslint-disable-next-line no-underscore-dangle
    calling_deployable_name: appConfig?._meta?.deployable,
    // eslint-disable-next-line no-underscore-dangle
    calling_deployable_version: appConfig?._meta?.version,
    ...extraProps,
  };

  const handleTrackInteraction = ({
    impressionSectionOverride = '',
    impressionPositionOverride = '',
  }: {
    impressionSectionOverride?: string;
    impressionPositionOverride?: string;
  } = {}) => {
    // track interaction for Apply Now button
    if (isApplyNowButtonType) {
      trackSegmentTrackEvent('Element Interaction', pageViewId, {
        ...marketplaceButtonProps,
      });
      return;
    }

    // track interaction for CTA button
    if (enableInteractionTracking) {
      const overrideProps = {
        ...marketplaceButtonProps,
      };
      if (impressionSectionOverride) {
        overrideProps.impression_section = impressionSectionOverride;
      }
      if (impressionPositionOverride) {
        overrideProps.impression_position = impressionPositionOverride;
      }
      trackSegmentTrackEvent('Element Interaction', pageViewId, {
        ...impressionViewProps,
        ...overrideProps,
        ...(subLocation && { sub_location: subLocation }),
        entity_name: selectedMarketplaceOffer?.name || entityName,
      });
    }
  };
  return {
    impressionViewProps,
    marketplaceButtonProps,
    handleTrackInteraction,
  };
};
